import type {
  NotificationKind,
  NotificationView,
} from '@/lib/api/generated/model';
import type { SettingsSection } from '@/features/settings';
import type { TaskTab } from '@/features/tasks/links';

import { runThreadHash } from '@/features/tasks/links';

import { tabOf, threadMessageOf } from './copy';

/** Where opening a notification goes (spec 131 FR-005). */
export type NotificationTarget =
  | {
      kind: 'board';
      workspaceId: string;
      workflowKey: string;
      taskId: string;
      tab: TaskTab;
      /** A section of the drawer to land on, by its element id. */
      hash?: string;
    }
  | {
      kind: 'tasks';
      workspaceId: string;
      taskId: string;
      tab: TaskTab;
      hash?: string;
    }
  | { kind: 'usage'; workspaceId: string }
  | { kind: 'triggers'; workspaceId: string }
  | { kind: 'connections'; workspaceId: string }
  /** The person's own linked accounts, on the Commits settings (spec 159). */
  | { kind: 'commits' }
  /** A workspace the person was added to: its home (spec 187). */
  | { kind: 'workspace'; workspaceId: string }
  /**
   * Who is in a workspace now: its Settings (spec 187), at the section the
   * notification's action is in (spec 193 US6).
   */
  | { kind: 'settings'; workspaceId: string; section: SettingsSection }
  /** The console's workspaces without an active owner (spec 191). */
  | { kind: 'admin-workspaces' };

/** The kinds about the person's own linked forge account (spec 159 US2). */
const OWN_ACCOUNT_KINDS: ReadonlySet<NotificationKind> = new Set([
  'forge_account_broken',
  'forge_account_expiring',
  'forge_account_revoked',
  'forge_account_required',
  'forge_account_requested',
]);

/**
 * A task's own board when its workflow is known, the Tasks page's drawer when
 * it is not (the drawer opens by task id alone there), and the page that
 * answers a workspace event otherwise.
 */
export function notificationTarget(
  notification: NotificationView,
  workflowKey: string | null | undefined,
): NotificationTarget {
  const workspaceId = notification.workspace.id;
  if (notification.task_id) {
    const tab = tabOf(notification);
    // Waiting tasks land on the parent's list of them, Start all at its head
    // (spec 140 US3). A message on a run's thread lands on that run, scrolled
    // to the message (spec 149 FR-006).
    //
    // `runThreadHash` is the tasks feature's leaf module rather than its index,
    // as `searchParams` is: the index brings in `TaskDrawer`, which imports
    // this feature back.
    const thread = threadMessageOf(notification);
    const hash =
      notification.kind === 'linked_tasks_waiting' ||
      notification.kind === 'scheduler_failed' ||
      notification.kind === 'scheduler_stalled'
        ? 'children'
        : thread
          ? runThreadHash(thread.executionId, thread.messageId)
          : undefined;
    return workflowKey
      ? {
          kind: 'board',
          workspaceId,
          workflowKey,
          taskId: notification.task_id,
          tab,
          ...(hash ? { hash } : {}),
        }
      : {
          kind: 'tasks',
          workspaceId,
          taskId: notification.task_id,
          tab,
          ...(hash ? { hash } : {}),
        };
  }
  // An escalation (spec 138) goes where what it escalates goes.
  const kind =
    notification.kind === 'escalated'
      ? notification.payload.escalated_kind
      : notification.kind;
  if (kind === 'trigger_failed') return { kind: 'triggers', workspaceId };
  if (kind === 'connection_failed') return { kind: 'connections', workspaceId };
  if (kind && OWN_ACCOUNT_KINDS.has(kind)) return { kind: 'commits' };
  if (kind === 'workspace_member_added')
    return { kind: 'workspace', workspaceId };
  if (kind === 'workspace_without_owner') return { kind: 'admin-workspaces' };
  // A request is answered in Requests to join; a removal shows, and a review
  // is done, in Members.
  if (kind === 'workspace_access_requested')
    return { kind: 'settings', workspaceId, section: 'requests' };
  if (
    kind === 'workspace_member_removed' ||
    kind === 'workspace_members_review'
  )
    return { kind: 'settings', workspaceId, section: 'members' };
  return { kind: 'usage', workspaceId };
}
